import { List } from '@hub/list'
import React from 'react'
import {
  NavigationContextProvider,
  useNavigationContext,
} from './navigation-context'

type NavigationBarProps = {
  data?: unknown
  onMouseEnter?: React.MouseEventHandler
}

export const NavigationBar: React.FC<
  React.PropsWithChildren<NavigationBarProps>
> = ({ children, onMouseEnter }) => {
  const {
    overlayContentRef,
    trigger,
    activeChild: activeBar,
    registerChild,
  } = useNavigationContext()
  const [activeChild, setActiveChild] = React.useState<number | null>(null)
  const symbol = React.useRef(Symbol())
  const isOpen = React.useMemo(() => activeBar === symbol.current, [activeBar])
  React.useEffect(() => {
    registerChild?.(symbol.current, activeChild !== null)
  }, [activeChild, registerChild])

  const items = React.useMemo(
    () =>
      React.Children.map(children, (child, index) => {
        if (!React.isValidElement(child)) {
          return child
        }
        return (
          <NavigationContextProvider
            value={{
              isBar: true,
              overlayContentRef,
              isOpen: isOpen && index === activeChild,
              onOpen() {
                setActiveChild(index)
                registerChild?.(symbol.current, true)
              },
              onClose() {
                registerChild?.(symbol.current, false)
                setActiveChild(null)
              },
              onMouseEnter() {
                if (trigger === 'hover' || activeBar !== undefined) {
                  setActiveChild(index)
                  registerChild?.(symbol.current, true)
                }
              },
            }}
          >
            {child}
          </NavigationContextProvider>
        )
      }),
    [
      activeBar,
      activeChild,
      children,
      isOpen,
      overlayContentRef,
      registerChild,
      trigger,
    ]
  )

  return (
    <List
      sx={{ direction: 'row', display: 'flex', alignItems: 'center' }}
      onMouseEnter={onMouseEnter}
    >
      {items}
    </List>
  )
}
